import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CalendarClock, Video, MapPin, Phone, Mail, ExternalLink, User, Trash2, CalendarSync, Plus, Send, AlertTriangle, ChevronsUpDown, Check, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const dayKey = (iso) => new Date(iso).toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
const dayLabel = (iso) => { const s = new Date(iso).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Rome" }); return s.charAt(0).toUpperCase() + s.slice(1); };
const hhmm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });

// ---- Calendario: helper griglia + colori per attore ----
const ACTOR_PALETTE = ["#f97316", "#FDD003", "#ec4899", "#ef4444", "#22c55e", "#a3e635", "#8b5cf6", "#06b6d4", "#3b82f6", "#14b8a6"];
function actorColor(uid) {
  if (!uid) return "#94a3b8";
  const s = String(uid);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return ACTOR_PALETTE[h % ACTOR_PALETTE.length];
}
const WD = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - day); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const keyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const eventKey = (iso) => keyOf(new Date(iso));
const HOURS = Array.from({ length: 14 }, (_, i) => 7 + i); // 07:00 - 20:00
const DAY_SLOTS = [];
for (let _h = 7; _h <= 20; _h++) { DAY_SLOTS.push({ h: _h, m: 0 }); DAY_SLOTS.push({ h: _h, m: 30 }); }
const pad2 = (n) => String(n).padStart(2, "0");

function EntityCombobox({ items, value, placeholder, onChange, asyncSearch, onQuery, emptyHint, testid }) {
  const [open, setOpen] = useState(false);
  const sel = (items || []).find((i) => i.id === value);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o && asyncSearch && onQuery) onQuery(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal mt-1" data-testid={testid}>
          <span className="truncate">{sel ? sel.label : <span className="text-muted-foreground">{placeholder}</span>}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={!asyncSearch}>
          <CommandInput placeholder="Cerca…" data-testid={`${testid}-search`} onValueChange={asyncSearch && onQuery ? onQuery : undefined} />
          <CommandList>
            <CommandEmpty>{emptyHint || "Nessun risultato"}</CommandEmpty>
            <CommandGroup>
              {(items || []).map((i) => (
                <CommandItem key={i.id} value={`${i.label} ${i.sub || ""} ${i.id}`} onSelect={() => { onChange(i.id, i); setOpen(false); }} data-testid={`${testid}-opt-${i.id}`}>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm truncate">{i.label}</span>
                    {i.sub && <span className="text-[11px] text-muted-foreground truncate">{i.sub}</span>}
                  </div>
                  {value === i.id && <Check className="h-4 w-4 ml-auto text-emerald-500 shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function RescheduleDialog({ appt, onDone }) {
  const [open, setOpen] = useState(false);
  const [freeVal, setFreeVal] = useState("");
  const [mode, setMode] = useState(appt.mode || "online");
  const [saving, setSaving] = useState(false);
  const openDialog = () => { setOpen(true); setFreeVal(""); setMode(appt.mode || "online"); };
  const submit = async () => {
    if (!freeVal) return;
    setSaving(true);
    try {
      await api.patch(`/appointments/${appt.id}/reschedule`, { start: freeVal, mode });
      toast.success("Appuntamento riprogrammato");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? openDialog() : setOpen(false))}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`agenda-reschedule-${appt.id}`}><CalendarSync className="h-4 w-4 mr-1.5" /> Riprogramma</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Riprogramma appuntamento</DialogTitle>
          <DialogDescription>{appt.name} — attuale: {dayLabel(appt.start)} alle {hhmm(appt.start)}. Scegli la modalità e un nuovo orario.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Modalità</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1.5" data-testid="reschedule-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="online" data-testid="reschedule-mode-online">Online (Google Meet)</SelectItem>
                <SelectItem value="visita" data-testid="reschedule-mode-visita">In sede (visita in azienda)</SelectItem>
              </SelectContent>
            </Select>
            {mode !== (appt.mode || "online") && (
              <p className="text-[11px] text-amber-600 mt-1.5" data-testid="reschedule-mode-changed">
                {mode === "online" ? "Passaggio a Online: verrà creato un link Google Meet (il consulente assegnato deve avere Google collegato)." : "Passaggio a In sede: l'appuntamento sarà presso lo stabilimento."}
              </p>
            )}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Data e ora</Label>
            <Input type="datetime-local" value={freeVal} onChange={(e) => setFreeVal(e.target.value)} data-testid="reschedule-free-datetime" className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!freeVal || saving} className="font-semibold" data-testid="reschedule-confirm-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CalendarSync className="h-4 w-4 mr-1.5" />} Conferma nuova data
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewInviteDialog({ leads, onDone }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("lead");
  const [leadId, setLeadId] = useState("");
  const [pQuery, setPQuery] = useState("");
  const [pResults, setPResults] = useState([]);
  const [pSel, setPSel] = useState(null);
  const [cQuery, setCQuery] = useState("");
  const [cResults, setCResults] = useState([]);
  const [cSel, setCSel] = useState(null);
  const [start, setStart] = useState("");
  const [dur, setDur] = useState("30");
  const [mode, setMode] = useState("online");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target !== "prospect" || pQuery.trim().length < 2) { setPResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/prospects?search=${encodeURIComponent(pQuery.trim())}&include_promotori=true`).then((r) => setPResults((r.data.items || []).slice(0, 20))).catch(() => setPResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [pQuery, target]);

  useEffect(() => {
    if (target !== "cliente" || cQuery.trim().length < 2) { setCResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/clienti?q=${encodeURIComponent(cQuery.trim())}`).then((r) => setCResults((r.data.items || []).slice(0, 20))).catch(() => setCResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [cQuery, target]);

  const reset = () => { setTarget("lead"); setLeadId(""); setPQuery(""); setPResults([]); setPSel(null); setCQuery(""); setCResults([]); setCSel(null); setStart(""); setAddress(""); setMode("online"); setDur("30"); };

  const submit = async () => {
    if (target === "lead" && !leadId) { toast.error("Seleziona un lead"); return; }
    if (target === "prospect" && !pSel) { toast.error("Seleziona un prospect"); return; }
    if (target === "cliente" && !cSel) { toast.error("Seleziona un cliente"); return; }
    if (!start) { toast.error("Seleziona la data/ora"); return; }
    if (mode === "cliente" && !address.trim()) { toast.error("Inserisci l'indirizzo della visita"); return; }
    setSaving(true);
    try {
      const payload = { start, duration_min: Number(dur), mode, address: mode === "cliente" ? address.trim() : undefined };
      if (target === "prospect") payload.prospect_id = pSel.id;
      else if (target === "cliente") payload.cliente_id = cSel.id;
      else payload.lead_id = leadId;
      await api.post("/appointments/manual", payload);
      toast.success(target === "prospect" ? "Appuntamento fissato per il prospect" : target === "cliente" ? "Appuntamento fissato per il cliente" : "Invito inviato al lead");
      setOpen(false); reset();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="font-semibold" data-testid="agenda-new-invite-btn"><Plus className="h-4 w-4 mr-1.5" /> Nuovo invito</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="new-invite-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo appuntamento</DialogTitle>
          <DialogDescription>Scegli un <b>Lead</b>, un <b>Prospect</b> o un <b>Cliente</b> e l'orario. Per gli inviti online l'evento con link Google Meet viene creato sul calendario del <b>consulente assegnato</b>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={target === "lead" ? "default" : "outline"} onClick={() => setTarget("lead")} data-testid="invite-target-lead">Lead</Button>
            <Button type="button" size="sm" variant={target === "prospect" ? "default" : "outline"} onClick={() => setTarget("prospect")} data-testid="invite-target-prospect">Prospect</Button>
            <Button type="button" size="sm" variant={target === "cliente" ? "default" : "outline"} onClick={() => setTarget("cliente")} data-testid="invite-target-cliente">Cliente</Button>
          </div>
          {target === "lead" ? (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Lead</Label>
              <EntityCombobox testid="invite-lead-select" placeholder="Cerca e seleziona un lead…" value={leadId}
                items={(leads || []).map((l) => ({ id: l.id, label: l.name, sub: l.email }))} onChange={(id) => setLeadId(id)} />
            </div>
          ) : target === "prospect" ? (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Prospect</Label>
              <EntityCombobox testid="invite-prospect-select" placeholder="Scrivi per cercare un prospect…" asyncSearch value={pSel?.id || ""} onQuery={setPQuery}
                emptyHint={pQuery.trim().length < 2 ? "Digita almeno 2 caratteri…" : "Nessun prospect trovato"}
                items={[
                  ...(pSel && !pResults.some((p) => p.id === pSel.id) ? [{ id: pSel.id, label: pSel.name }] : []),
                  ...pResults.map((p) => ({ id: p.id, label: p.company_name, sub: [p.city, p.email].filter(Boolean).join(" · ") })),
                ]}
                onChange={(id, item) => { if (item) setPSel({ id: item.id, name: item.label }); }} />
            </div>
          ) : (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</Label>
              <EntityCombobox testid="invite-cliente-select" placeholder="Scrivi per cercare un cliente…" asyncSearch value={cSel?.id || ""} onQuery={setCQuery}
                emptyHint={cQuery.trim().length < 2 ? "Digita almeno 2 caratteri…" : "Nessun cliente trovato"}
                items={[
                  ...(cSel && !cResults.some((c) => c.id === cSel.id) ? [{ id: cSel.id, label: cSel.name }] : []),
                  ...cResults.map((c) => ({ id: c.id, label: c.company_name, sub: [c.city, c.email].filter(Boolean).join(" · ") })),
                ]}
                onChange={(id, item) => { if (item) setCSel({ id: item.id, name: item.label }); }} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Data e ora</Label>
              <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="invite-datetime-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Durata</Label>
              <Select value={dur} onValueChange={setDur}>
                <SelectTrigger data-testid="invite-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minuti</SelectItem>
                  <SelectItem value="45">45 minuti</SelectItem>
                  <SelectItem value="60">60 minuti</SelectItem>
                  <SelectItem value="90">90 minuti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Modalità</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="invite-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Online (Google Meet)</SelectItem>
                <SelectItem value="visita">In visita allo stabilimento</SelectItem>
                <SelectItem value="cliente">In visita presso cliente/cantiere</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "cliente" && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Indirizzo visita</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Via, numero civico, città" data-testid="invite-address-input" />
              <p className="text-[11px] text-muted-foreground mt-1.5">L'indirizzo verrà usato come luogo dell'evento su Google Calendar e nell'email.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="font-semibold" data-testid="invite-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} {target === "lead" ? "Invia invito" : "Fissa appuntamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const VIEWS = [
  { key: "day", label: "Giorno" },
  { key: "week", label: "Settimana" },
  { key: "2weeks", label: "2 settimane" },
  { key: "month", label: "Mese" },
  { key: "list", label: "Elenco" },
];

function ModeBadge({ mode }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${mode === "online" ? "bg-primary/10 text-primary" : "bg-secondary text-foreground"}`}>
      {mode === "online" ? <><Video className="h-3 w-3" /> Online</> : mode === "cliente" ? <><MapPin className="h-3 w-3" /> Presso cliente/cantiere</> : <><MapPin className="h-3 w-3" /> In visita</>}
    </span>
  );
}

export default function Calendario() {
  const { user } = useAuth();
  const isAdmin = ["admin", "team_leader"].includes(user?.role);
  const [view, setView] = useState("2weeks");
  const [anchor, setAnchor] = useState(() => new Date());
  const [rangeItems, setRangeItems] = useState(null);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [showGoogle, setShowGoogle] = useState(true);
  const [items, setItems] = useState(null);
  const [history, setHistory] = useState(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [generating, setGenerating] = useState(null);

  const range = useMemo(() => {
    const a = new Date(anchor);
    if (view === "day") {
      const s = new Date(a); s.setHours(0, 0, 0, 0);
      return { gridStart: s, gridEnd: addDays(s, 1) };
    }
    if (view === "month") {
      const first = new Date(a.getFullYear(), a.getMonth(), 1);
      const last = new Date(a.getFullYear(), a.getMonth() + 1, 0);
      return { gridStart: startOfWeek(first), gridEnd: addDays(startOfWeek(last), 7) };
    }
    const gridStart = startOfWeek(a);
    return { gridStart, gridEnd: addDays(gridStart, view === "week" ? 7 : 14) };
  }, [anchor, view]);

  const loadRange = useCallback(() => {
    if (view === "list") return;
    setRangeItems(null);
    const from = range.gridStart.toISOString();
    const to = range.gridEnd.toISOString();
    api.get(`/appointments/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      .then((r) => setRangeItems(r.data.appointments || []))
      .catch(() => setRangeItems([]));
  }, [view, range]);

  const loadList = useCallback(() => {
    api.get("/appointments/agenda").then((r) => setItems(r.data.appointments || [])).catch(() => setItems([]));
    api.get("/appointments/history").then((r) => setHistory(r.data.appointments || [])).catch(() => setHistory([]));
  }, []);

  useEffect(() => { loadRange(); }, [loadRange]);
  useEffect(() => {
    loadList();
    api.get("/leads").then((r) => setLeads(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role !== "partner_commerciale"))).catch(() => {});
  }, [loadList]);

  const reloadAll = () => { loadRange(); loadList(); };

  const matchUser = useCallback((a) => userFilter === "all" ? true : userFilter === "unassigned" ? !a.assigned_to : a.assigned_to === userFilter, [userFilter]);

  const removeAppt = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/appointments/${id}`);
      toast.success("Appuntamento eliminato");
      setDetail(null);
      reloadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(null); }
  };

  const generateMeet = async (id) => {
    setGenerating(id);
    try {
      const r = await api.post(`/appointments/${id}/generate-meet`);
      toast.success(r.data?.meet_link ? "Link Meet generato" : "Evento creato (link Meet in elaborazione)");
      setDetail(null);
      reloadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(null); }
  };

  const shift = (dir) => setAnchor((prev) => {
    const a = new Date(prev);
    if (view === "day") return addDays(a, dir);
    if (view === "month") return new Date(a.getFullYear(), a.getMonth() + dir, 1);
    return addDays(a, dir * (view === "week" ? 7 : 14));
  });

  const title = useMemo(() => {
    if (view === "day") { const s = anchor.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); }
    if (view === "month") { const s = anchor.toLocaleDateString("it-IT", { month: "long", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); }
    const end = addDays(range.gridEnd, -1);
    const f = (d) => d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
    return `${f(range.gridStart)} – ${f(end)} ${end.getFullYear()}`;
  }, [view, anchor, range]);

  const visibleItems = useMemo(() => (rangeItems || []).filter(matchUser), [rangeItems, matchUser]);
  const actors = useMemo(() => {
    const list = (users || []).map((u) => ({ id: u.id, name: u.name || u.email }));
    if ((rangeItems || []).some((a) => !a.assigned_to)) list.push({ id: "unassigned", name: "Non assegnato" });
    return list;
  }, [users, rangeItems]);

  const weeks = useMemo(() => {
    if (view === "list") return [];
    const nWeeks = Math.round((range.gridEnd - range.gridStart) / (7 * 86400000));
    const arr = [];
    for (let w = 0; w < nWeeks; w++) {
      const days = [];
      for (let d = 0; d < 7; d++) days.push(addDays(range.gridStart, w * 7 + d));
      arr.push(days);
    }
    return arr;
  }, [range, view]);

  const eventsOn = useCallback((day) => visibleItems.filter((a) => eventKey(a.start) === keyOf(day)).sort((x, y) => x.start.localeCompare(y.start)), [visibleItems]);
  const todayKey = keyOf(new Date());

  // Eventi Google Calendar dei consulenti nel range (Sync Google)
  useEffect(() => {
    if (view === "list" || !showGoogle || userFilter === "unassigned") { setGoogleEvents([]); return; }
    const from = range.gridStart.toISOString();
    const to = range.gridEnd.toISOString();
    const uq = userFilter !== "all" ? `&user_id=${userFilter}` : "";
    api.get(`/appointments/google-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${uq}`)
      .then((r) => setGoogleEvents(r.data.events || []))
      .catch(() => setGoogleEvents([]));
  }, [view, range, showGoogle, userFilter]);

  const googleOn = useCallback((day) => (showGoogle ? googleEvents : []).filter((e) => eventKey(e.start) === keyOf(day)).sort((x, y) => String(x.start).localeCompare(String(y.start))), [googleEvents, showGoogle]);

  const GoogleChip = ({ e }) => (
    <a href={e.html_link || "#"} target="_blank" rel="noreferrer" data-testid={`cal-gevent-${e.id}`} title={`Google · ${e.user_name}`}
      className="block rounded px-1.5 py-1 mb-1 text-[11px] leading-tight truncate border border-dashed border-muted-foreground/40 bg-muted/40 text-muted-foreground hover:opacity-80">
      <span className="font-semibold">{e.all_day ? "Giorno" : hhmm(e.start)}</span> {e.summary}
    </a>
  );

  // Trascina per riprogrammare: sposta l'appuntamento su un altro giorno (mantiene l'orario)
  const onDropDay = async (day, ev) => {
    ev.preventDefault();
    let a; try { a = JSON.parse(ev.dataTransfer.getData("text/plain")); } catch { return; }
    if (!a || !a.id || keyOf(day) === eventKey(a.start)) return;
    const d = new Date(a.start);
    const start = `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    try { await api.patch(`/appointments/${a.id}/reschedule`, { start, mode: a.mode || "online" }); toast.success("Appuntamento spostato"); reloadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Spostamento non riuscito"); }
  };
  const dayDrop = (day) => ({ onDragOver: (e) => e.preventDefault(), onDrop: (e) => onDropDay(day, e) });

  const Chip = ({ a }) => {
    const c = actorColor(a.assigned_to);
    return (
      <button draggable onDragStart={(ev) => ev.dataTransfer.setData("text/plain", JSON.stringify({ id: a.id, start: a.start, mode: a.mode }))} onClick={() => setDetail(a)} data-testid={`cal-event-${a.id}`}
        className="w-full text-left rounded px-1.5 py-1 mb-1 text-[11px] leading-tight truncate hover:opacity-80 transition-opacity cursor-grab active:cursor-grabbing"
        style={{ backgroundColor: `${c}22`, borderLeft: `3px solid ${c}` }}>
        <span className="font-semibold">{hhmm(a.start)}</span> {a.name}
      </button>
    );
  };

  // list-view grouping
  const listGroups = {};
  (items || []).filter(matchUser).forEach((a) => { (listGroups[dayKey(a.start)] ||= []).push(a); });
  const listKeys = Object.keys(listGroups).sort();
  const shownHistory = (history || []).filter(matchUser);

  return (
    <div data-testid="calendario-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Calendario</h1>
          <p className="text-xs text-muted-foreground">{isAdmin ? "Tutti gli appuntamenti della divisione" : "I tuoi appuntamenti"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-[180px] h-9" data-testid="agenda-user-filter"><SelectValue placeholder="Filtra per utente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="agenda-user-filter-all">Tutti gli utenti</SelectItem>
              <SelectItem value="unassigned" data-testid="agenda-user-filter-unassigned">Non assegnati</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`agenda-user-filter-${u.id}`}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <NewInviteDialog leads={leads} onDone={reloadAll} />
        </div>
      </header>

      <div className="p-4 lg:p-8 space-y-4">
        {/* Toolbar: viste + navigazione */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-lg border border-border p-0.5" data-testid="cal-view-toggle">
            {VIEWS.map((v) => (
              <button key={v.key} onClick={() => setView(v.key)} data-testid={`cal-view-${v.key}`}
                className={cn("px-3 py-1.5 text-sm rounded-md transition-colors", view === v.key ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground")}>
                {v.label}
              </button>
            ))}
          </div>
          {view !== "list" && (
            <div className="flex items-center gap-2">
              <Button variant={showGoogle ? "default" : "outline"} size="sm" onClick={() => setShowGoogle((v) => !v)} data-testid="cal-google-toggle"><CalendarDays className="h-4 w-4 mr-1.5" /> Google</Button>
              <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="cal-today-btn">Oggi</Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="cal-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="cal-next-btn"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </div>

        {/* Legenda attori (colori) */}
        {view !== "list" && actors.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="cal-legend">
            {actors.map((ac) => {
              const active = userFilter === (ac.id === "unassigned" ? "unassigned" : ac.id);
              return (
                <button key={ac.id} onClick={() => setUserFilter(active ? "all" : (ac.id === "unassigned" ? "unassigned" : ac.id))}
                  className={cn("inline-flex items-center gap-1.5 text-xs rounded-full px-2 py-0.5 transition-colors", active ? "ring-1 ring-primary bg-primary/5" : "hover:bg-secondary")}
                  data-testid={`cal-legend-${ac.id}`}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: actorColor(ac.id === "unassigned" ? null : ac.id) }} />
                  {ac.name}
                </button>
              );
            })}
          </div>
        )}

        {view !== "list" ? (
          <Card className="p-3 lg:p-4" data-testid="cal-grid">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-bold text-lg" data-testid="cal-range-title">{title}</h2>
            </div>
            {rangeItems === null ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : view === "week" ? (
              /* Vista Settimana: griglia oraria */
              <div className="overflow-x-auto">
                <div className="min-w-[720px]">
                  <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
                    <div />
                    {weeks[0].map((d) => (
                      <div key={keyOf(d)} className={cn("text-center pb-2 text-[11px] font-bold uppercase tracking-wide", keyOf(d) === todayKey ? "text-primary" : "text-muted-foreground")}>
                        {WD[(d.getDay() + 6) % 7]} <span className="block text-[10px] font-normal">{d.getDate()}/{d.getMonth() + 1}</span>
                      </div>
                    ))}
                  </div>
                  {HOURS.map((h) => (
                    <div key={h} className="grid border-t border-border/60" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
                      <div className="text-[10px] text-muted-foreground py-2 pr-2 text-right">{String(h).padStart(2, "0")}:00</div>
                      {weeks[0].map((d) => {
                        const evs = eventsOn(d).filter((a) => new Date(a.start).getHours() === h);
                        const gev = googleOn(d).filter((e) => !e.all_day && new Date(e.start).getHours() === h);
                        return (
                          <div key={keyOf(d) + h} className="min-h-[44px] p-1 border-l border-border/60" {...dayDrop(d)}>
                            {evs.map((a) => <Chip key={a.id} a={a} />)}
                            {gev.map((e) => <GoogleChip key={e.id} e={e} />)}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ) : view === "day" ? (
              /* Vista Giorno: fasce orarie 30 min per pianificare visite fitte */
              <div className="max-w-2xl">
                {googleOn(anchor).filter((e) => e.all_day).map((e) => <GoogleChip key={e.id} e={e} />)}
                <div className="rounded-lg border border-border divide-y divide-border/60" data-testid="cal-day-grid">
                  {DAY_SLOTS.map((s) => {
                    const inSlot = (iso) => { const d = new Date(iso); return d.getHours() === s.h && (s.m === 0 ? d.getMinutes() < 30 : d.getMinutes() >= 30); };
                    const evs = eventsOn(anchor).filter((a) => inSlot(a.start));
                    const gev = googleOn(anchor).filter((e) => !e.all_day && inSlot(e.start));
                    return (
                      <div key={`${s.h}:${s.m}`} className="grid" style={{ gridTemplateColumns: "64px 1fr" }} {...dayDrop(anchor)}>
                        <div className="text-[11px] text-muted-foreground py-2 pr-2 text-right">{pad2(s.h)}:{pad2(s.m)}</div>
                        <div className="min-h-[38px] p-1 border-l border-border/60">
                          {evs.map((a) => <Chip key={a.id} a={a} />)}
                          {gev.map((e) => <GoogleChip key={e.id} e={e} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Viste 2 settimane / Mese: griglia a giorni */
              <div className="space-y-2">
                <div className="grid grid-cols-7 gap-2">
                  {WD.map((w) => <div key={w} className="text-center text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{w}</div>)}
                </div>
                {weeks.map((wk, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-2">
                    {wk.map((day) => {
                      const evs = eventsOn(day);
                      const gevs = googleOn(day);
                      const isToday = keyOf(day) === todayKey;
                      const dim = view === "month" && day.getMonth() !== anchor.getMonth();
                      return (
                        <div key={keyOf(day)} data-testid={`cal-cell-${keyOf(day)}`} {...dayDrop(day)}
                          className={cn("min-h-[110px] rounded-lg border p-2 overflow-hidden", isToday ? "border-primary ring-1 ring-primary/40" : "border-border", dim && "opacity-40")}>
                          <div className="text-[11px] text-muted-foreground mb-1">{day.getDate()}/{day.getMonth() + 1}</div>
                          {evs.map((a) => <Chip key={a.id} a={a} />)}
                          {gevs.map((e) => <GoogleChip key={e.id} e={e} />)}
                          {!evs.length && !gevs.length ? <div className="text-muted-foreground/40 text-xs">—</div> : null}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : (
          /* Vista Elenco: lista prossimi + storico */
          <>
            {items === null ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : listKeys.length === 0 ? (
              <Card className="max-w-xl rounded-lg border-border p-8 text-center" data-testid="agenda-empty">
                <CalendarClock className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Nessun appuntamento in programma.</p>
              </Card>
            ) : (
              <div className="space-y-8 max-w-3xl" data-testid="agenda-list">
                {listKeys.map((k) => (
                  <div key={k}>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-primary font-bold mb-3">{dayLabel(listGroups[k][0].start)}</div>
                    <div className="space-y-3">
                      {listGroups[k].map((a) => (
                        <Card key={a.id} className="rounded-lg border-border p-4" data-testid={`agenda-item-${a.id}`}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className="text-center min-w-[54px]">
                                <div className="text-lg font-heading font-bold leading-none">{hhmm(a.start)}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">{hhmm(a.end)}</div>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <Link to={a.prospect_id ? `/prospects/${a.prospect_id}` : a.cliente_id ? `/clienti/${a.cliente_id}` : `/leads/${a.lead_id}`} className="font-semibold hover:text-primary transition-colors" data-testid={`agenda-lead-${a.id}`}>{a.name}</Link>
                                  {a.prospect_id && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500 font-bold">Prospect</span>}
                                  {a.cliente_id && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 font-bold">Cliente</span>}
                                </div>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                                  <ModeBadge mode={a.mode} />
                                  {a.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {a.email}</span>}
                                  {a.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</span>}
                                  {isAdmin && a.assigned_to_name && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {a.assigned_to_name}</span>}
                                </div>
                                {a.mode !== "online" && a.location && <div className="text-xs text-muted-foreground mt-1"><MapPin className="h-3 w-3 inline mr-1" />{a.location}</div>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {a.meet_link ? (
                                <a href={a.meet_link} target="_blank" rel="noreferrer">
                                  <Button size="sm" className="font-semibold" data-testid={`agenda-meet-${a.id}`}><Video className="h-4 w-4 mr-1.5" /> Entra nel Meet <ExternalLink className="h-3 w-3 ml-1.5" /></Button>
                                </a>
                              ) : a.assigned_google_connected ? (
                                <Button size="sm" variant="outline" className="font-semibold" disabled={generating === a.id} onClick={() => generateMeet(a.id)} data-testid={`agenda-generate-meet-${a.id}`}>
                                  {generating === a.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Video className="h-4 w-4 mr-1.5" />} Genera link Meet
                                </Button>
                              ) : a.mode === "online" ? (
                                <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-600 max-w-[190px] text-right" data-testid={`agenda-nomeet-${a.id}`}>
                                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                  <span><b>{a.assigned_to_name || "Il consulente assegnato"}</b> non ha Google collegato</span>
                                </span>
                              ) : null}
                              <RescheduleDialog appt={a} onDone={reloadAll} />
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" disabled={deleting === a.id} data-testid={`agenda-delete-${a.id}`} aria-label="Elimina appuntamento">
                                    {deleting === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent data-testid="agenda-delete-dialog">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Eliminare l'appuntamento?</AlertDialogTitle>
                                    <AlertDialogDescription>Stai per eliminare l'appuntamento con <b>{a.name}</b> del {dayLabel(a.start)} alle {hhmm(a.start)}. Lo slot tornerà disponibile.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => removeAppt(a.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`agenda-confirm-delete-${a.id}`}>Elimina</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-12 max-w-4xl" data-testid="agenda-history">
              <h2 className="text-sm font-heading font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">Appuntamenti fatti</h2>
              {history === null ? (
                <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : shownHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="agenda-history-empty">Nessun appuntamento passato.</p>
              ) : (
                <div className="rounded-lg border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-secondary/40 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        <th className="text-left font-semibold px-3 py-2">Data</th>
                        <th className="text-left font-semibold px-3 py-2">Ora</th>
                        <th className="text-left font-semibold px-3 py-2">Cliente</th>
                        <th className="text-left font-semibold px-3 py-2">Modalità</th>
                        {isAdmin && <th className="text-left font-semibold px-3 py-2">Consulente</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {shownHistory.map((a) => (
                        <tr key={a.id} className="border-t border-border hover:bg-secondary/20" data-testid={`agenda-history-item-${a.id}`}>
                          <td className="px-3 py-2 whitespace-nowrap">{dayLabel(a.start)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{hhmm(a.start)}</td>
                          <td className="px-3 py-2">
                            <Link to={a.prospect_id ? `/prospects/${a.prospect_id}` : a.cliente_id ? `/clienti/${a.cliente_id}` : `/leads/${a.lead_id}`} className="font-medium hover:text-primary transition-colors">{a.name}</Link>
                            {a.prospect_id && <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500 font-bold">Prospect</span>}
                            {a.cliente_id && <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 font-bold">Cliente</span>}
                          </td>
                          <td className="px-3 py-2"><span className="inline-flex items-center gap-1 text-xs text-muted-foreground">{a.mode === "online" ? <><Video className="h-3 w-3" /> Online</> : a.mode === "cliente" ? <><MapPin className="h-3 w-3" /> Presso cliente</> : <><MapPin className="h-3 w-3" /> In visita</>}</span></td>
                          {isAdmin && <td className="px-3 py-2 text-muted-foreground">{a.assigned_to_name || "—"}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Dettaglio appuntamento (click su chip) */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-md" data-testid="cal-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Link to={detail.prospect_id ? `/prospects/${detail.prospect_id}` : detail.cliente_id ? `/clienti/${detail.cliente_id}` : `/leads/${detail.lead_id}`} className="hover:text-primary transition-colors" data-testid="cal-detail-link">{detail.name}</Link>
                  {detail.prospect_id && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500 font-bold">Prospect</span>}
                  {detail.cliente_id && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 font-bold">Cliente</span>}
                </DialogTitle>
                <DialogDescription>{dayLabel(detail.start)} · {hhmm(detail.start)}–{hhmm(detail.end)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
                  <ModeBadge mode={detail.mode} />
                  {detail.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {detail.email}</span>}
                  {detail.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {detail.phone}</span>}
                </div>
                {detail.assigned_to_name && <div className="inline-flex items-center gap-1.5 text-xs"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: actorColor(detail.assigned_to) }} /> {detail.assigned_to_name}</div>}
                {detail.mode !== "online" && detail.location && <div className="text-xs text-muted-foreground"><MapPin className="h-3 w-3 inline mr-1" />{detail.location}</div>}
              </div>
              <DialogFooter className="flex-wrap gap-2 sm:justify-start">
                {detail.meet_link ? (
                  <a href={detail.meet_link} target="_blank" rel="noreferrer"><Button size="sm" className="font-semibold" data-testid="cal-detail-meet"><Video className="h-4 w-4 mr-1.5" /> Entra nel Meet <ExternalLink className="h-3 w-3 ml-1.5" /></Button></a>
                ) : detail.assigned_google_connected ? (
                  <Button size="sm" variant="outline" disabled={generating === detail.id} onClick={() => generateMeet(detail.id)} data-testid="cal-detail-generate-meet">
                    {generating === detail.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Video className="h-4 w-4 mr-1.5" />} Genera link Meet
                  </Button>
                ) : null}
                <RescheduleDialog appt={detail} onDone={() => { reloadAll(); setDetail(null); }} />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" disabled={deleting === detail.id} data-testid="cal-detail-delete"><Trash2 className="h-4 w-4 mr-1.5" /> Elimina</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="cal-detail-delete-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare l'appuntamento?</AlertDialogTitle>
                      <AlertDialogDescription>Stai per eliminare l'appuntamento con <b>{detail.name}</b> del {dayLabel(detail.start)} alle {hhmm(detail.start)}. Lo slot tornerà disponibile.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => removeAppt(detail.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="cal-detail-confirm-delete">Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
